import React from "react";
import { Link } from "react-router-dom";
import "./Legal.css";

function TermsOfService() {
  return (
    <div className="legal-page">
      <Link to="/" className="back-home">
        ← Back to Home
      </Link>
      <div className="legal-content">
        <h1 className="legal-title">Terms of Service</h1>

        <p className="legal-subtitle">
          Version Effective Date: December 27, 2025
        </p>

        <p className="legal-intro">
          Thank you for using M31-V1's services. These Terms of Service
          ("Terms") govern the relationship between M31-V1 LLC ("M31-V1", "we",
          "our") and the entity or person ("Customer", "You", "Your") accessing
          or using our services, applications, or platform through our website
          at m31v1.com (the "Site") or by any other means (together, the
          "Services"). These Terms explain your rights with respect to any
          content, data, or materials you might create or access through the
          Services (the "Content"), your use of the Services, and other
          important topics including dispute resolution. Our privacy policy,
          available on our website, outlines how we handle your data. Please
          carefully read these Terms along with our privacy policy and all other
          referenced documents. Together, these form a binding agreement between
          us (the "Agreement").
        </p>

        <p className="legal-intro">
          This Agreement becomes effective when you access or use the Services
          (the "Effective Date"). This Agreement may be updated periodically.
          Continued use of the Services constitutes acceptance of any updated
          terms. If you do not agree to this Agreement, please discontinue use
          of the Services immediately.
        </p>

        <section className="legal-section">
          <h2>1. Service Availability and Quality</h2>
          <p>
            We continuously work to improve and enhance our Services. The
            Services are subject to modification and change, including but not
            limited to functionality, features, algorithms, and user interface.
            We make no guarantees regarding the Services' quality, stability,
            uptime, reliability, or continued availability. Please do not create
            dependencies on any specific attributes of the Services. We will not
            be liable to you or any third parties for any harm caused by your
            reliance on the Services.
          </p>
          <p>
            The Services are provided on an "AS IS" and "AS AVAILABLE" basis,
            WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or
            implied, including without limitation any warranties of
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, or
            NON-INFRINGEMENT. You are solely responsible for determining the
            appropriateness of using the Services and assume all risks
            associated with their use.
          </p>
          <p>
            M31-V1 reserves the right to suspend or terminate your access to the
            Services at any time for any reason, including but not limited to
            violation of these Terms. You may not use the Services to develop or
            offer competitive products or services. You may not reverse
            engineer, decompile, or disassemble any aspect of the Services. You
            may not use automated tools or bots to access or interact with the
            Services. You may not resell, redistribute, or transfer access to
            the Services. Each user may maintain only one account, and accounts
            may not be shared.
          </p>
          <p>
            You may not use the Services to violate the intellectual property
            rights of others, including copyright, patent, trademark, or trade
            secret rights. Violations may result in legal action or permanent
            termination of access to the Services.
          </p>
          <p>
            We reserve the right to investigate complaints or reported
            violations of this Agreement and take appropriate action, including
            reporting suspected unlawful activity to law enforcement,
            regulators, or other authorities, and disclosing relevant
            information such as user profiles, email addresses, usage history,
            IP addresses, and traffic data.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Age Requirements</h2>
          <p>
            By accessing the Services, you confirm that you are at least 13
            years old and meet the minimum age of digital consent in your
            jurisdiction. If you meet the minimum age to access the Services but
            are not old enough to have authority to consent to these Terms, your
            parent or guardian must agree to these Terms on your behalf.
          </p>
          <p>
            If you are a parent or legal guardian allowing your minor to use the
            Services, these Terms apply to you and you are responsible for your
            minor's activity on the Services.
          </p>
          <p>
            M31-V1 strives to maintain appropriate content standards, but our
            Services utilize artificial intelligence and machine learning
            technologies that may not always function as expected. We make no
            guarantees regarding the suitability of any content or outputs for
            any particular purpose.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Your Information</h2>
          <p>
            By using the Services, you may provide M31-V1 with personal
            information including your email address, username, billing
            information, and any content, data, or materials you input, upload,
            or create through the Services. Our collection and use of this
            information is governed by our Privacy Policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. Content and Intellectual Property Rights</h2>

          <h3>Your Rights</h3>
          <p>
            Subject to the terms of this Agreement and applicable law, you
            retain ownership of content you create using the Services. However,
            this ownership is subject to:
          </p>
          <ul>
            <li>Any obligations imposed by this Agreement</li>
            <li>The rights of third parties</li>
            <li>
              Any license requirements specified by your subscription tier
            </li>
          </ul>
          <p>
            Please consult your own legal advisor regarding intellectual
            property rights in your jurisdiction. Your rights to content created
            through the Services persist even if you subsequently downgrade or
            cancel your subscription.
          </p>

          <h3>Your Responsibilities</h3>
          <p>
            You are responsible for all content you provide, input, upload, or
            generate through the Services, including ensuring it does not
            violate any applicable laws or this Agreement, and that you have all
            necessary rights and permissions to provide such content.
          </p>

          <h3>Rights You Grant to M31-V1</h3>
          <p>
            By using the Services, you grant M31-V1, its affiliates, successors,
            and assigns a perpetual, worldwide, non-exclusive, sublicensable,
            royalty-free, irrevocable license to use, reproduce, prepare
            derivative works of, publicly display, publicly perform, sublicense,
            and distribute content you input into or create through the Services
            for purposes of operating, improving, and promoting our Services.
            This license survives termination of this Agreement.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. DMCA and Takedown Policy</h2>

          <h3>Notification Procedures</h3>
          <p>
            We respect intellectual property rights. If you believe material on
            the Services infringes your copyright or trademark, please send a
            notice to <a href="mailto:legal@m31v1.com">legal@m31v1.com</a> with
            the subject "Takedown Request" and include:
          </p>
          <ul>
            <li>Your physical or electronic signature</li>
            <li>
              Identification of the copyrighted work you believe has been
              infringed
            </li>
            <li>
              Identification of the allegedly infringing material with
              sufficient detail to locate it
            </li>
            <li>
              Your contact information (name, address, phone number, email)
            </li>
            <li>
              A statement of good faith belief that the use is unauthorized
            </li>
            <li>A statement that the information in your notice is accurate</li>
            <li>
              A statement, under penalty of perjury, that you are authorized to
              act on behalf of the copyright owner
            </li>
          </ul>
          <p>
            Upon receipt of a valid notice, we may remove or disable access to
            the accused material, notify the accused party, and terminate access
            for repeat infringers.
          </p>
          <p>
            Please be aware that knowingly misrepresenting that material
            infringes your copyright may subject you to liability for damages
            under Section 512(f) of the DMCA.
          </p>

          <h3>Counter-Notification Procedures</h3>
          <p>
            If you believe material was removed by mistake or misidentification,
            you may file a counter-notification to{" "}
            <a href="mailto:legal@m31v1.com">legal@m31v1.com</a> including:
          </p>
          <ul>
            <li>Your physical or electronic signature</li>
            <li>Identification of removed material and its prior location</li>
            <li>Your contact information</li>
            <li>
              A statement under penalty of perjury of good faith belief in
              mistake or misidentification
            </li>
            <li>
              A statement consenting to jurisdiction of Federal District Court
              and acceptance of service from the complainant
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6. Dispute Resolution and Governing Law</h2>
          <p>
            Any dispute, controversy, or claim arising from or relating to these
            Terms ("Dispute") will be resolved by binding arbitration rather
            than in court. The parties will first attempt in good faith to
            settle any Dispute within 30 days. If unresolved, the Dispute shall
            be resolved by binding arbitration under the rules of the American
            Arbitration Association. The arbitration will be conducted in
            English in California, USA.
          </p>
          <p>
            By agreeing to arbitration, you and M31-V1 waive any right to trial
            by jury. The arbitrator may order equitable or injunctive relief
            consistent with the Agreement's remedies and limitations. The
            arbitral award will be final and binding.
          </p>
          <p>
            Each party will bear its own legal fees and expenses regardless of
            the arbitrator's decision.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Payment and Billing</h2>
          <p>
            We may invoice you for Services through a third-party payment
            processor. The payment processor's terms govern payment processing
            and supersede this Agreement in case of conflict solely with respect
            to payments.
          </p>
          <p>
            You may cancel your subscription at any time. We reserve the right
            to modify or discontinue any aspect of the Services, including
            pricing and features. We also reserve the right to terminate your
            access for violation of these Terms or inappropriate use. You will
            not be refunded for the current subscription period, but will not be
            charged after the current period ends.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Acceptable Use Policy</h2>
          <p>
            You agree to use the Services responsibly and in compliance with all
            applicable laws. Specifically, you may not:
          </p>
          <ul>
            <li>Use the Services to harass, abuse, or harm others</li>
            <li>
              Generate or distribute content that is illegal, fraudulent,
              defamatory, or obscene
            </li>
            <li>
              Use the Services to violate others' privacy or intellectual
              property rights
            </li>
            <li>
              Attempt to deceive or defraud anyone using content from the
              Services
            </li>
            <li>
              Use the Services for political campaigns or to influence elections
            </li>
            <li>
              Upload illegal content or use the Services for illegal purposes
            </li>
            <li>Distribute others' personal information without permission</li>
            <li>
              Intentionally mislead recipients about the nature or source of
              generated content
            </li>
          </ul>
          <p>
            Violations may result in immediate suspension or termination of
            access to the Services. We reserve the right to determine what
            constitutes a violation in our sole discretion.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Limitation of Liability and Indemnity</h2>
          <p>
            We provide the Services as is, and make no promises or guarantees
            about them.
          </p>
          <p>
            You understand and agree that we will not be liable to you or any
            third party for any loss of profits, use, goodwill, or data, or for
            any incidental, indirect, special, consequential, or exemplary
            damages, however arising. Our aggregate liability under this
            Agreement will not exceed the amount you paid for the Services
            during the 12 months preceding the claim.
          </p>
          <p>
            You are responsible for your use of the Services. If you harm
            someone or get into a dispute with someone else, we will not be
            involved.
          </p>
          <p>
            To the extent permitted by law, you will indemnify and hold harmless
            M31-V1, our affiliates, and our personnel from any costs, losses,
            liabilities, and expenses (including attorneys' fees) arising from
            third-party claims related to your use of the Services or violation
            of these Terms.
          </p>
          <p>
            Any cause of action arising from or related to the Services must
            commence within one (1) year after the cause of action accrues.
            Otherwise, such cause of action is permanently barred.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Miscellaneous</h2>
          <p>
            <strong>Force Majeure.</strong> Neither party will be liable for
            failure or delay in performance caused by circumstances beyond its
            reasonable control, including acts of God, natural disasters,
            terrorism, riots, or war.
          </p>
          <p>
            <strong>No Agency.</strong> This Agreement does not create any
            agency, partnership, or joint venture between the parties.
          </p>
          <p>
            <strong>Severability.</strong> If any part of this Agreement is
            invalid, illegal, or unenforceable, the rest of the Agreement will
            remain in effect.
          </p>
          <p>
            <strong>No Third-Party Beneficiaries.</strong> This Agreement does
            not confer any benefits on any third party unless expressly stated.
          </p>
          <p>
            <strong>Survival.</strong> Sections of this Agreement that a
            reasonable person would expect to survive termination will survive,
            particularly those relating to intellectual property, privacy,
            liability, and dispute resolution.
          </p>
          <p>
            <strong>Governing Law.</strong> This Agreement shall be governed by
            the laws of the State of California, USA, without regard to conflict
            of law principles. All disputes will be resolved according to the
            arbitration agreement above.
          </p>
        </section>

        <div className="legal-footer">
          <p>
            For questions about these Terms, please contact us at{" "}
            <a href="mailto:legal@m31v1.com">legal@m31v1.com</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;
